import { useEffect, useState, type ReactNode } from "react";
import { useUser } from "../../Data/UserContext";
import { getAuthHeaders } from "../../utils/authHeaders";
import { normalizeSessionPayload } from "../../utils/sessionUser";

type ShopFrame = {
  id: number;
  owned?: boolean;
  name: string;
  imageUrl: string;
  priceAmount: number | null;
  priceCurrency: "CR8_RBY" | "COINS" | null;
};

const CURRENCY_ICON = {
  CR8_RBY: "/assets/currency-ruby.png",
  COINS: "/assets/currency-coin.png",
} as const;
const CURRENCY_NAME = { CR8_RBY: "rubies", COINS: "coins" } as const;

type ShopState = {
  buyable: ShopFrame[];
  owned: ShopFrame[];
  activeFrameId: number | null;
};

const EMPTY_STATE: ShopState = { buyable: [], owned: [], activeFrameId: null };

function priced(list: unknown): ShopFrame[] {
  if (!Array.isArray(list)) return [];
  return (list as ShopFrame[]).filter(
    (f) => f.priceAmount != null && f.priceCurrency != null
  );
}

function parseShopState(data: unknown): ShopState {
  const d = (data ?? {}) as {
    shopFrames?: unknown;
    ownedShopFrames?: unknown;
    activeFrameId?: unknown;
  };
  return {
    buyable: priced(d.shopFrames),
    owned: priced(d.ownedShopFrames),
    activeFrameId: typeof d.activeFrameId === "number" ? d.activeFrameId : null,
  };
}

/**
 * "Avatar frames" shop section; renders nothing unless the player has shop frames to buy or already owns.
 * Owned frames are listed after the buyable ones, marked as owned with a Wear button.
 */
export default function AvatarFramesShop({
  rubies,
  coins,
}: {
  rubies: number;
  coins: number;
}) {
  const { user, setUser } = useUser();
  const [shop, setShop] = useState<ShopState>(EMPTY_STATE);
  const [buyingId, setBuyingId] = useState<number | null>(null);
  const [message, setMessage] = useState<{
    text: string;
    error: boolean;
  } | null>(null);

  useEffect(() => {
    if (!user?.userId) {
      setShop(EMPTY_STATE);
      return undefined;
    }
    let cancelled = false;
    fetch(`${process.env.API_URL}/account/avatar-frames`, {
      headers: getAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setShop(parseShopState(data));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.userId]);

  if (!shop.buyable.length && !shop.owned.length && !message) return null;

  async function refreshSession() {
    const verify = await fetch(`${process.env.API_URL}/verifysession`, {
      headers: getAuthHeaders(),
    });
    if (verify.status === 200 && setUser) {
      setUser(
        normalizeSessionPayload(
          (await verify.json()) as Record<string, unknown>
        )
      );
    }
  }

  async function wear(frame: ShopFrame) {
    if (buyingId != null) return;
    setBuyingId(frame.id);
    setMessage(null);
    try {
      const res = await fetch(`${process.env.API_URL}/account/avatar-frame`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ choice: frame.id }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage({
          text: data?.message || "Could not change your frame.",
          error: true,
        });
        return;
      }
      setShop(parseShopState(data));
      setMessage({
        text: `${frame.name} is now on your avatar.`,
        error: false,
      });
      await refreshSession();
    } catch {
      setMessage({
        text: "Something went wrong. Please try again.",
        error: true,
      });
    } finally {
      setBuyingId(null);
    }
  }

  async function buy(frame: ShopFrame) {
    if (buyingId != null) return;
    setBuyingId(frame.id);
    setMessage(null);
    try {
      const res = await fetch(
        `${process.env.API_URL}/account/avatar-frames/${frame.id}/buy`,
        {
          method: "POST",
          headers: getAuthHeaders(),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage({
          text: data?.message || "Could not buy this frame.",
          error: true,
        });
        return;
      }
      setShop(parseShopState(data));
      setMessage({
        text: `${frame.name} is yours and now on your avatar.`,
        error: false,
      });
      window.dispatchEvent(new CustomEvent("walletUpdate"));
      await refreshSession();
    } catch {
      setMessage({
        text: "Something went wrong. Please try again.",
        error: true,
      });
    } finally {
      setBuyingId(null);
    }
  }

  return (
    <>
      <div className="flex h-[30px] w-[100%] flex-row items-center justify-center gap-[8px] bg-[#401A6E] py-4 font-anton text-[14px] font-[400] leading-[15px] text-white">
        <span className="font-anton">AVATAR FRAMES</span>
      </div>
      {message ? (
        <p
          className={`px-3 pb-2 text-center font-chakra text-[11px] normal-case ${
            message.error ? "text-[#f87171]" : "text-[#32F78D]"
          }`}
        >
          {message.text}
        </p>
      ) : null}
      {shop.buyable.length || shop.owned.length ? (
        <div className="flex w-[100%] items-stretch justify-evenly gap-0.5 overflow-x-auto overflow-y-hidden bg-inherit px-[3px]">
          {shop.buyable.map((frame) => {
            const currency = frame.priceCurrency!;
            const price = frame.priceAmount!;
            const canAfford =
              (currency === "CR8_RBY" ? rubies : coins) >= price;
            return (
              <FrameCard key={frame.id} frame={frame} avatar={user?.avatar}>
                <button
                  type="button"
                  onClick={() => void buy(frame)}
                  disabled={buyingId != null || !canAfford}
                  aria-label={`Buy ${frame.name} for ${price} ${CURRENCY_NAME[currency]}`}
                  className={`${BUTTON_CLASS} ${
                    canAfford ? "bg-lime" : "bg-gray-300"
                  }`}
                >
                  {buyingId === frame.id ? (
                    "…"
                  ) : canAfford ? (
                    <>
                      Buy for {price.toLocaleString()}
                      <img
                        src={CURRENCY_ICON[currency]}
                        alt=""
                        className="inline-block h-4 w-4 object-contain"
                        aria-hidden
                      />
                    </>
                  ) : (
                    `Need ${price.toLocaleString()} ${CURRENCY_NAME[currency]}`
                  )}
                </button>
              </FrameCard>
            );
          })}
          {shop.owned.map((frame) => {
            const wearing = shop.activeFrameId === frame.id;
            return (
              <FrameCard
                key={frame.id}
                frame={frame}
                avatar={user?.avatar}
                ownedLabel={frame.owned ? "Owned" : "Unlocked"}
              >
                <button
                  type="button"
                  onClick={() => void wear(frame)}
                  disabled={buyingId != null || wearing}
                  aria-label={
                    wearing
                      ? `${frame.name} is on your avatar`
                      : `Wear ${frame.name}`
                  }
                  className={`${BUTTON_CLASS} ${
                    wearing ? "bg-[#401A6E] text-white" : "bg-lime"
                  }`}
                >
                  {buyingId === frame.id ? "…" : wearing ? "Wearing" : "Wear"}
                </button>
              </FrameCard>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

const BUTTON_CLASS =
  "flex h-[40px] min-w-[100px] flex-row items-center justify-center gap-1 whitespace-nowrap px-2 text-center font-anton text-[14px] font-[400] uppercase text-[#1B062F] disabled:cursor-not-allowed";

function FrameCard({
  frame,
  avatar,
  ownedLabel,
  children,
}: {
  frame: ShopFrame;
  avatar?: string | null;
  ownedLabel?: string;
  children: ReactNode;
}) {
  return (
    <div className="relative flex w-full min-w-[140px] flex-col items-center justify-center gap-1 bg-white px-2 py-3">
      {ownedLabel ? (
        <span className="absolute left-1.5 top-1.5 bg-[#32F78D] px-1.5 py-0.5 font-anton text-[10px] uppercase leading-none text-[#1B062F]">
          {ownedLabel}
        </span>
      ) : null}
      <span className="relative block h-[72px] w-[72px] bg-[#2a2342]">
        {avatar ? (
          <img
            src={avatar}
            alt=""
            className="block h-full w-full object-cover"
            decoding="async"
          />
        ) : null}
        <img
          src={frame.imageUrl}
          alt=""
          className="pointer-events-none absolute inset-0 block h-full w-full"
          decoding="async"
          aria-hidden
        />
      </span>
      <p className="text-center font-anton text-[14px] font-[400] leading-tight text-[#1B062F]">
        {frame.name}
      </p>
      <p className="text-center font-chakra text-[11px] normal-case text-[#1B062F]">
        Avatar frame
      </p>
      {children}
    </div>
  );
}
